// @ts-strict-ignore
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Tooltip } from '@node-space/ui-components'
import { Approval } from 'constants/General'
import { useApprovalsContext } from 'hooks/context/useApprovalsContext'
import { ApprovalStatuses, UnapprovableTransactions } from 'types/approvals'

interface ApprovalHeaderButtonsProps {
  activeTab: string
  hasCheckedPendingTransactions?: boolean
  cannotApprove: UnapprovableTransactions
}

const ApprovalHeaderButtons = ({
  activeTab,
  hasCheckedPendingTransactions,
  cannotApprove,
}: ApprovalHeaderButtonsProps) => {
  const { t } = useTranslation()

  const {
    permissions,
    approvalsConfig: { handleManageApproversModal, handleApprovalDecisionModal },
  } = useApprovalsContext()

  const hasSelfInitiatedApprovals = !!cannotApprove?.selfInitiatedTransactions?.length
  const hasUnapprovableTransactions = !!cannotApprove?.cannotApproveTransactions?.length
  const cannotApproveTransaction = hasSelfInitiatedApprovals || hasUnapprovableTransactions

  const ApproveButton = useMemo(() => {
    return (
      <Button
        disabled={!hasCheckedPendingTransactions || cannotApproveTransaction}
        onClick={() =>
          handleApprovalDecisionModal({
            isVisible: true,
            decision: Approval.DECISION.APPROVE as 'APPROVE',
          })
        }
      >
        {t('approvals.approve')}
      </Button>
    )
  }, [hasCheckedPendingTransactions, cannotApproveTransaction])

  return (
    <Box flex className="sm:p-0 gap-3">
      <Box width="full" className="sm:w-auto">
        <Button variant="secondary" onClick={() => handleManageApproversModal({ isVisible: true })}>
          {permissions?.canEdit ? t('approvals.manageApprovers') : t('approvals.viewApprovers')}
        </Button>
      </Box>

      {permissions?.canManageApproval && activeTab == ApprovalStatuses.PENDING && (
        <>
          <Box width="full" className="sm:w-auto">
            {cannotApproveTransaction ? (
              <Tooltip
                type="complex"
                bodyContent={
                  hasSelfInitiatedApprovals
                    ? t('approvals.hasSelfInitiatedApprovalsDisclaimer')
                    : t('approvals.unapprovableDisclaimer')
                }
                position="bottom"
              >
                {ApproveButton}
              </Tooltip>
            ) : (
              ApproveButton
            )}
          </Box>
          <Box width="full" className="sm:w-auto">
            <Button
              disabled={!hasCheckedPendingTransactions}
              onClick={() =>
                handleApprovalDecisionModal({
                  isVisible: true,
                  decision: Approval.DECISION.REJECT as 'REJECT',
                })
              }
            >
              {t('approvals.reject')}
            </Button>
          </Box>
        </>
      )}
    </Box>
  )
}

export default React.memo(ApprovalHeaderButtons)
